import { useEffect, useState } from "react";
import type { LutSlotInfo } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { Icon } from "../../../kit";
import { MiniBtn, Row, TextActionInput } from "../../inspector/rows";
import { pickFile } from "../../../../util/download";

/** Slot-local work in flight — the LUT RPCs are per slot, so a global flag would
 * lock all three slots while one of them parses a file. */
type SlotBusy = "upload" | "clear" | null;

const errText = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "LUT operation failed.";

/** Three colour-LUT slots — load a .cube / strip-PNG per slot or clear it.
 * The global enable/intensity live on the LUT section rows; this is the
 * texture management UI (iJewel's 3 LUT slots). */
export function LutControl() {
  const { client } = useViewer();
  const r = client.ring;
  const [slots, setSlots] = useState<(LutSlotInfo | null)[]>([null, null, null]);
  const [busy, setBusy] = useState<SlotBusy[]>([null, null, null]);
  const [errors, setErrors] = useState<(string | null)[]>([null, null, null]);
  const [listError, setListError] = useState<string | null>(null);

  const setBusyAt = (slot: number, v: SlotBusy) =>
    setBusy((prev) => prev.map((b, i) => (i === slot ? v : b)));
  const setErrorAt = (slot: number, v: string | null) =>
    setErrors((prev) => prev.map((e, i) => (i === slot ? v : e)));

  useEffect(() => {
    // A failed listing used to be swallowed, leaving all three slots reading
    // "empty" even when the viewer holds LUTs — say so instead.
    r.listLuts().then(
      (s) => { setSlots(s); setListError(null); },
      (e: unknown) => setListError(errText(e)),
    );
  }, [r]);

  const upload = async (slot: number) => {
    setBusyAt(slot, "upload");
    setErrorAt(slot, null);
    // pickFile settles when the work finishes, so awaiting it holds the spinner
    // for the whole read+parse+upload rather than just the dialog opening. The
    // RPC is caught INSIDE onFile: pickFile chains `.finally` onto the promise
    // we return, so a rejection escaping here becomes an unhandled rejection.
    await pickFile(".cube,.png", async (f) => {
      try {
        setSlots(await r.loadLutFile(slot, f));
      } catch (e) {
        // luts.ts throws "invalid .cube LUT" / "LUT image must be n²×n" — the
        // slot would otherwise stay "empty" with no explanation.
        setErrorAt(slot, errText(e));
      }
    });
    setBusyAt(slot, null);
  };

  const clear = async (slot: number) => {
    setBusyAt(slot, "clear");
    setErrorAt(slot, null);
    try {
      setSlots(await r.clearLut(slot));
    } catch (e) {
      setErrorAt(slot, errText(e));
    } finally {
      setBusyAt(slot, null);
    }
  };

  return (
    <div className="flex flex-col gap-1.5 px-3 py-1">
      {listError ? <span className="text-[11px] text-ed-red">{listError}</span> : null}
      {[0, 1, 2].map((slot) => {
        const info = slots[slot];
        const b = busy[slot];
        return (
          <div key={slot} className="flex flex-col gap-1 rounded border border-ed-border bg-ed-row p-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-medium text-ed-text">LUT {slot + 1}</span>
              {info ? (
                <span className="flex items-center gap-1 text-[11px] text-ed-text-3">
                  {info.name} · {info.size}³
                  <button
                    onClick={() => void clear(slot)}
                    // Any in-flight work on this slot owns it: a clear racing an
                    // upload would have whichever resolved last win the slot.
                    disabled={b !== null}
                    aria-busy={b === "clear" || undefined}
                    className="text-ed-text-3 hover:text-ed-text disabled:cursor-not-allowed disabled:opacity-60"
                    title="Clear"
                  >
                    <Icon
                      name={b === "clear" ? "progress_activity" : "close"}
                      size={13}
                      className={b === "clear" ? "animate-spin" : undefined}
                    />
                  </button>
                </span>
              ) : (
                <span className="text-[11px] text-ed-text-3">empty</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <MiniBtn
                icon="upload"
                busy={b === "upload"}
                disabled={b !== null}
                onClick={() => void upload(slot)}
              >
                {b === "upload" ? "Loading…" : "Upload"}
              </MiniBtn>
            </div>
            <TextActionInput
              placeholder=".cube / .png URL"
              action="Load"
              // Returned, not voided: TextActionInput spins while the fetch runs,
              // keeps the URL on failure ("LUT fetch failed: 404") and surfaces
              // the rejection inline instead of dropping it on the floor.
              onSubmit={(u) => {
                setErrorAt(slot, null);
                return r.loadLutUrl(slot, u).then(setSlots);
              }}
            />
            {errors[slot] ? <span className="text-[11px] text-ed-red">{errors[slot]}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

/** Lightweight wrapper so the section row can pass through ctx/read. */
export function LutControlRow() {
  return (
    <Row stacked>
      <LutControl />
    </Row>
  );
}
